// dependency-graph-ignore-file
import { AppearanceTypes, AutoDismissFlag, FlagGroup } from '@atlaskit/flag';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import InfoIcon from '@atlaskit/icon/core/migration/information--info';
import SuccessIcon from '@atlaskit/icon/core/migration/success--check-circle';
import WarningIcon from '@atlaskit/icon/core/migration/warning';
import { token } from '@atlaskit/tokens';
import React, { ReactNode } from 'react';
import { useSnapshot } from 'valtio';

import { FlagsManager } from '../contracts/flags-manager';
import { Flag } from '../types';

const unreachable = (value: never) => {
  throw new Error(`Not implemented: ${value}`);
};

interface DefaultFlagGroupProps {
  flags: { data: Flag[] };
  handleFlagDismiss: FlagsManager['handleFlagDismiss'];
}

/**
 * A default flag group that renders flags using the `@atlaskit/flag` component.
 * This is used locally in the databases app, though it will not be implemented when we
 * defer flags to Confluence.
 */
export function DefaultFlagGroup(props: DefaultFlagGroupProps) {
  const snapshot = useSnapshot(props.flags);

  const getIcon = (appearance: AppearanceTypes): ReactNode => {
    switch (appearance) {
      case 'info':
        return (
          <InfoIcon
            spacing="spacious"
            label=""
            LEGACY_secondaryColor={token('color.background.neutral.bold', '#44546F')}
          />
        );
      case 'warning':
        return (
          <WarningIcon
            spacing="spacious"
            label=""
            LEGACY_secondaryColor={token('color.background.warning.bold', '#F5CD47')}
          />
        );
      case 'error':
        return (
          <ErrorIcon
            spacing="spacious"
            label=""
            LEGACY_secondaryColor={token('color.background.danger.bold', '#C9372C')}
          />
        );
      case 'success':
        return (
          <SuccessIcon
            spacing="spacious"
            label=""
            LEGACY_secondaryColor={token('color.background.success.bold', '#1F845A')}
          />
        );
      case 'normal':
        return (
          <InfoIcon
            spacing="spacious"
            label=""
            color={token('color.icon.information', '#1D7AFC')}
          />
        );
      default:
        unreachable(appearance);
    }
  };

  return (
    <FlagGroup
      onDismissed={(flagId) => {
        props.handleFlagDismiss(flagId);
      }}
    >
      {snapshot.data.map((flag) => {
        const flagAppearance = flag.appearance ?? 'normal';

        return (
          <AutoDismissFlag
            appearance={flagAppearance}
            id={flag.id}
            testId="database-flag"
            icon={flag.icon ? flag.icon() : getIcon(flagAppearance)}
            key={flag.id}
            title={flag.title}
            description={flag.description}
            actions={flag.actions ? [...flag.actions] : undefined}
          />
        );
      })}
    </FlagGroup>
  );
}
